import { MONTHS } from "@utils/Constants";
import { getMonthBoundaries, getYearBoundaries } from "@utils/Utils";

/**
 * Descriptores de los reportes que componen el paquete contable.
 *
 * El paquete mensual y el anual son el MISMO trabajo: lo unico que cambia es como
 * se calcula el rango de fechas. Por eso todo el flujo (offcanvas, formulario,
 * orquestador y lambda) recibe un `mode` y no se duplica por periodo.
 *
 * Ojo con la convencion de fin de rango: el reporte de ventas usa rango
 * semiabierto (endDate = dia 1 del mes siguiente) mientras que compras y gastos
 * usan el ultimo dia del mes. Se replica tal cual la usan hoy `SellReportForm`,
 * `BuyReportForm` y `ExpenseReportForm`, para que el paquete cuadre exactamente
 * con los reportes individuales.
 */

export type AccountingPackageMode = "month" | "year";

export type AccountingPackageReportKey = "ventas" | "compras" | "gastos";

export type AccountingPackageReport = {
    key: AccountingPackageReportKey,
    label: string,
    printPath: string,
    excelPath: string
}

/** Valor con el que los formularios de reporte piden "todos" en un filtro de entidad. */
const ALL_ENTITIES = "-2";
/** El paquete no detalla las columnas de IVA: el Excel debe cuadrar con el PDF. */
const DETALLAR_IVA = "0";

const getBoundaries = (
    mode: AccountingPackageMode,
    year: string,
    month: string | undefined,
    useFirstMonthDate: boolean
) => {
    if (mode === "year") {
        return getYearBoundaries(year, useFirstMonthDate);
    }
    return getMonthBoundaries(year, `${month}`, useFirstMonthDate);
}

export const buildAccountingPackageReports = (
    mode: AccountingPackageMode,
    year: string,
    month?: string
): AccountingPackageReport[] => {
    const sellPeriod = getBoundaries(mode, year, month, true);
    const buyPeriod = getBoundaries(mode, year, month, false);

    return [
        {
            key: "ventas",
            label: "Ventas",
            printPath: `/user/reports/print/sellByPeriod/${ALL_ENTITIES}/${ALL_ENTITIES}/${sellPeriod.startDate}/${sellPeriod.endDate}`,
            excelPath: `/user/reports/excel/sellByPeriod/${ALL_ENTITIES}/${ALL_ENTITIES}/${sellPeriod.startDate}/${sellPeriod.endDate}/${DETALLAR_IVA}`,
        },
        {
            key: "compras",
            label: "Compras",
            printPath: `/user/reports/print/buyByMonth/${ALL_ENTITIES}/${buyPeriod.startDate}/${buyPeriod.endDate}`,
            excelPath: `/user/reports/excel/buyByMonth/${ALL_ENTITIES}/${buyPeriod.startDate}/${buyPeriod.endDate}`,
        },
        {
            key: "gastos",
            label: "Gastos",
            printPath: `/user/reports/print/expenseByMonth/${buyPeriod.startDate}/${buyPeriod.endDate}`,
            excelPath: `/user/reports/excel/expenseByMonth/${buyPeriod.startDate}/${buyPeriod.endDate}`,
        },
    ];
}

/** Texto del periodo para titulos y asunto del correo: "Septiembre del 2026" o "2026". */
export const buildAccountingPackageLabel = (
    mode: AccountingPackageMode,
    year: string,
    month?: string
): string => {
    if (mode === "year") {
        return `${year}`;
    }
    return `${MONTHS[`${month}`] || ""} del ${year}`.trim();
}

/** Sufijo del nombre del ZIP: "2026-09" o "2026". */
export const buildAccountingPackagePeriodSlug = (
    mode: AccountingPackageMode,
    year: string,
    month?: string
): string => {
    if (mode === "year") {
        return `${year}`;
    }
    return `${year}-${month}`;
}

/**
 * Rutas en S3 de los archivos intermedios. La lambda deriva estas mismas rutas a
 * partir del tenantId del token y del `stamp`, de modo que la mutation nunca
 * recibe keys arbitrarias.
 *
 * Los PDF los escribe el generador externo en el bucket `mavisoft-pdfs` (por eso
 * conservan el prefijo `${tenantId}/pdfs/` que ya usa el resto de la app); los
 * Excel los sube el navegador al bucket de `appStorage`, cuyo unico prefijo
 * escribible por usuarios autenticados es `suscription/`.
 */
export const buildAccountingPackagePdfKey = (tenantId: string, stamp: string, key: AccountingPackageReportKey): string =>
    `${tenantId}/pdfs/accounting-packages/${stamp}/${key}.pdf`;

export const buildAccountingPackageXlsxKey = (tenantId: string, stamp: string, key: AccountingPackageReportKey): string =>
    `suscription/${tenantId}/accounting-packages/${stamp}/${key}.xlsx`;
